/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  MmsUploadResult,
  ModerationOutcome,
  uploadMmsMediaFile,
  waitForMmsModeration,
} from '~/store/actions/mms';
import { useTranslation } from '~/dependencies';
import {
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  // Close as CloseIcon,
  Delete as DeleteIcon,
  ErrorOutline as ErrorOutlineIcon,
  Replay as ReplayIcon,
  Visibility as VisibilityIcon,
} from '@material-ui/icons';
import {
  BlockedThumbOverlay,
  // CloseButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DoneButton,
  ErrorLabel,
  ErrorRow,
  EyeOverlay,
  FileBadge,
  FileInfo,
  FileName,
  FileRow,
  FileSize,
  FileThumb,
  FileThumbImage,
  ImagePreviewDialog,
  ImagePreviewImg,
  ModerationLabel,
  ProcessingDots,
  ProgressBar,
  ProgressLabel,
  RemoveButton,
  RetryOverlay,
  SuccessRow,
  UuidLabel,
} from './styles';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type UploadStatus =
  | 'idle'
  | 'uploading'
  | 'moderating'
  | 'success'
  | 'blocked'
  | 'error';

const MMS_KEY_PREFIX = 'screens.conversation.mms.';

const MODERATION_REJECTED_KEY = 'moderationRejected';
const MODERATION_PENDING_KEY = 'moderationPending';
const MODERATION_UNVERIFIED_KEY = 'moderationUnverified';

const MODERATION_OUTCOME_KEYS: Record<
  Exclude<ModerationOutcome, 'approved'>,
  string
> = {
  rejected: MODERATION_REJECTED_KEY,
  unverifiable: MODERATION_UNVERIFIED_KEY,
  timeout: MODERATION_PENDING_KEY,
};

const FALLBACKS: Record<string, string> = {
  [MODERATION_REJECTED_KEY]:
    "This file was blocked by content moderation and can't be sent.",
  [MODERATION_UNVERIFIED_KEY]:
    "Content moderation couldn't confirm this file, so it can't be sent.",
  [MODERATION_PENDING_KEY]:
    'Still being checked by content moderation. Retry in a moment.',
  moderationBlockedNote:
    'Content moderation blocked {{count}} file(s) — remove them to continue',
  moderationInProgress: 'Content moderation in progress…',
  filesCannotBeSent:
    "{{count}} file(s) can't be sent — remove or retry to continue",
  unsupportedFileType: 'File type is not supported for MMS.',
  allFilesUploaded: 'All files uploaded ({{count}})',
  uploadingProgress: 'Uploading… ({{done}} / {{total}})',
  checkingMedia: 'Checking media…',
  checkingMediaCount: 'Checking media… ({{count}})',
  done: 'Done',
  removeBlockedFiles: 'Remove Blocked Files',
  removeFailedFiles: 'Remove Failed Files',
  retry: 'Retry',
  previewFile: 'Preview {{name}}',
  removeFile: 'Remove {{name}}',
  imagePreview: 'Image preview',
};

const UNSUPPORTED_FILE_TYPE_ERROR = 'File type is not supported for MMS.';

interface FileEntry {
  file: File;
  preview?: string;
  progress: number;
  status: UploadStatus;
  uploadResult?: MmsUploadResult;
  error?: string;
  /** i18n key for a moderation failure; takes precedence over `error`. */
  errorKey?: string;
  /**
   * Only meaningful while `status === 'blocked'`: false for an explicit
   * rejection (same bytes, same verdict), true when the verdict never arrived
   * and another attempt can still clear the file.
   */
  canRetry?: boolean;
}

export interface FileUploadResult {
  file: File;
  uploadResult: MmsUploadResult;
}

interface FileUploadProgressModalProps {
  files: File[];
  open: boolean;
  onClose: () => void;
  onRemove?: (file: File) => void;
  /** Called when all files finish uploading (including retries). */
  onComplete?: (results: FileUploadResult[]) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function FileUploadProgressModal({
  files,
  open,
  onClose,
  onRemove,
  onComplete,
}: FileUploadProgressModalProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  const { t } = useTranslation();
  const tr = (key: string, options?: Record<string, unknown>): string =>
    t(`${MMS_KEY_PREFIX}${key}`, { defaultValue: FALLBACKS[key], ...options });
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [expandedSrc, setExpandedSrc] = useState<string | null>(null);
  // Track which files are currently being uploaded to avoid duplicate runs
  const uploadingRef = useRef<Set<string>>(new Set());
  const cancelRef = useRef<Map<string, { cancelled: boolean }>>(new Map());
  const completedRef = useRef<string | null>(null);

  // Merge file list into entries, preserving existing upload state
  useEffect(() => {
    setEntries(prev => {
      const prevMap = new Map(prev.map(e => [e.file.name + e.file.size, e]));
      // Revoke object URLs for files that were removed from the list
      prev.forEach(e => {
        const key = e.file.name + e.file.size;
        if (!files.some(f => f.name + f.size === key) && e.preview) {
          URL.revokeObjectURL(e.preview);
        }
      });
      return files.map(file => {
        const key = file.name + file.size;
        return (
          prevMap.get(key) ?? {
            file,
            preview: file.type.startsWith('image/')
              ? URL.createObjectURL(file)
              : undefined,
            progress: 0,
            status: 'idle' as UploadStatus,
          }
        );
      });
    });
  }, [files]);

  // Revoke all object URLs and stop any in-flight moderation poll on unmount
  useEffect(() => {
    const cancelTokens = cancelRef.current;

    return () => {
      cancelTokens.forEach(token => {
        // eslint-disable-next-line no-param-reassign
        token.cancelled = true;
      });
      cancelTokens.clear();
      setEntries(prev => {
        prev.forEach(e => e.preview && URL.revokeObjectURL(e.preview));
        return [];
      });
    };
  }, []);

  // Reset UI state when modal closes (preserve upload state)
  useEffect(() => {
    if (!open) setExpandedSrc(null);
  }, [open]);

  useEffect(() => {
    if (open && files.length === 0) onClose();
  }, [open, files.length, onClose]);

  const settleEntry = useCallback((file: File, patch: Partial<FileEntry>) => {
    setEntries(prev =>
      prev.map(e => (e.file === file ? { ...e, ...patch } : e))
    );
  }, []);

  useEffect(() => {
    if (entries.length === 0) {
      completedRef.current = null;
      return;
    }

    const allApproved = entries.every(
      e => e.status === 'success' && e.uploadResult
    );

    if (!allApproved) {
      completedRef.current = null;
      return;
    }

    const signature = entries.map(e => e.uploadResult?.mediaId ?? '').join('|');

    if (completedRef.current === signature) return;
    completedRef.current = signature;

    onComplete?.(
      entries
        .filter(e => e.uploadResult)
        .map(e => ({ file: e.file, uploadResult: e.uploadResult! }))
    );
  }, [entries, onComplete]);

  const startUpload = useCallback(
    (file: File) => {
      const key = file.name + file.size;
      if (uploadingRef.current.has(key)) return;
      uploadingRef.current.add(key);

      const previous = cancelRef.current.get(key);
      if (previous) previous.cancelled = true;
      const cancelToken = { cancelled: false };
      cancelRef.current.set(key, cancelToken);

      setEntries(prev =>
        prev.map(e =>
          e.file === file
            ? {
                ...e,
                status: 'uploading',
                progress: 0,
                error: undefined,
                errorKey: undefined,
                canRetry: undefined,
              }
            : e
        )
      );

      dispatch(
        uploadMmsMediaFile(file, pct => {
          setEntries(prev =>
            prev.map(e => (e.file === file ? { ...e, progress: pct } : e))
          );
        })
      )
        .then(async (uploadResult: MmsUploadResult) => {
          uploadingRef.current.delete(key);
          if (cancelToken.cancelled) return;

          if (uploadResult.moderationStatus === 'rejected') {
            settleEntry(file, {
              status: 'blocked',
              errorKey: MODERATION_REJECTED_KEY,
              canRetry: false,
            });
            return;
          }

          if (uploadResult.moderationStatus !== 'approved') {
            setEntries(prev =>
              prev.map(e =>
                e.file === file
                  ? {
                      ...e,
                      status: 'moderating' as UploadStatus,
                      progress: 100,
                      uploadResult,
                    }
                  : e
              )
            );

            const outcome = await waitForMmsModeration(
              uploadResult.mediaId,
              () => cancelToken.cancelled
            );

            if (cancelToken.cancelled) return;

            if (outcome !== 'approved') {
              settleEntry(file, {
                status: 'blocked',
                errorKey: MODERATION_OUTCOME_KEYS[outcome],
                canRetry: outcome !== 'rejected',
              });
              return;
            }
          }

          settleEntry(file, {
            status: 'success',
            progress: 100,
            uploadResult,
          });
        })
        .catch((err: Error) => {
          uploadingRef.current.delete(key);
          if (cancelToken.cancelled) return;
          setEntries(prev =>
            prev.map(e =>
              e.file === file
                ? { ...e, status: 'error' as UploadStatus, error: err.message }
                : e
            )
          );
        });
    },
    [dispatch, settleEntry]
  );

  // Start idle uploads only while the modal is open
  useEffect(() => {
    if (!open) return;
    entries.filter(e => e.status === 'idle').forEach(e => startUpload(e.file));
  }, [entries, startUpload, open]);

  const handleRemove = useCallback(
    (file: File) => {
      const key = file.name + file.size;
      const cancelToken = cancelRef.current.get(key);
      if (cancelToken) cancelToken.cancelled = true;
      cancelRef.current.delete(key);
      uploadingRef.current.delete(key);

      setEntries(prev => {
        const entry = prev.find(e => e.file === file);
        if (entry?.preview) URL.revokeObjectURL(entry.preview);
        return prev.filter(e => e.file !== file);
      });
      onRemove?.(file);
    },
    [onRemove]
  );

  const handleRemoveBlocked = useCallback(() => {
    entries
      .filter(e => e.status === 'blocked')
      .forEach(e => handleRemove(e.file));
  }, [entries, handleRemove]);

  const doneCount = entries.filter(e => e.status === 'success').length;
  const errorCount = entries.filter(e => e.status === 'error').length;
  const blockedCount = entries.filter(e => e.status === 'blocked').length;
  const moderatingCount = entries.filter(e => e.status === 'moderating').length;
  const uploading = entries.some(
    e => e.status === 'uploading' || e.status === 'idle'
  );
  const inProgress = uploading || moderatingCount > 0;
  const allSettled = !inProgress;
  const canFinish = allSettled && errorCount === 0 && blockedCount === 0;
  const canRemoveBlocked = allSettled && blockedCount > 0;

  let titleText = tr('allFilesUploaded', { count: doneCount });
  if (uploading)
    titleText = tr('uploadingProgress', {
      done: doneCount,
      total: entries.length,
    });
  else if (moderatingCount > 0)
    titleText = tr('checkingMediaCount', { count: moderatingCount });
  else if (blockedCount > 0)
    titleText = tr('moderationBlockedNote', { count: blockedCount });
  else if (errorCount > 0)
    titleText = tr('filesCannotBeSent', { count: errorCount });

  const retryLabel = tr('retry');

  const translateErrorLabel = (entry: FileEntry) => {
    if (entry.errorKey) return tr(entry.errorKey);
    // FIXME: Checking with backend team to include error code in the response to avoid string matching
    if (entry.error?.includes(UNSUPPORTED_FILE_TYPE_ERROR))
      return tr('unsupportedFileType');
    return entry.error;
  };
  return (
    <>
      <Dialog
        open={open && files.length > 0}
        onClose={canFinish ? onClose : undefined}
        aria-labelledby="upload-progress-dialog"
        data-test-id="upload-progress-dialog">
        <DialogTitle disableTypography>
          <span className="title" data-test-id="upload-progress-title">
            {titleText}
          </span>
          {/* {allSettled && (
            <CloseButton aria-label="Close" size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </CloseButton>
          )} */}
        </DialogTitle>

        <DialogContent>
          {entries.map((entry, index) => {
            const { file, preview, progress, status, uploadResult } = entry;
            const isImage = file.type.startsWith('image/');
            const blocked = status === 'blocked';

            return (
              <FileRow
                key={file.name + file.size}
                data-test-id={`upload-progress-file-row-${index}`}>
                <FileThumb blocked={blocked}>
                  {isImage && preview ? (
                    <>
                      <FileThumbImage src={preview} alt={file.name} />
                      <EyeOverlay
                        aria-label={tr('previewFile', { name: file.name })}
                        data-test-id={`upload-progress-preview-${index}`}
                        onClick={() => setExpandedSrc(preview)}>
                        <VisibilityIcon fontSize="small" />
                      </EyeOverlay>
                    </>
                  ) : (
                    <FileBadge>{getExtension(file.name)}</FileBadge>
                  )}
                  {blocked &&
                    (entry.canRetry ? (
                      <RetryOverlay
                        type="button"
                        title={retryLabel}
                        aria-label={`${retryLabel} ${file.name}`}
                        data-test-id={`upload-progress-retry-${index}`}
                        onClick={() => startUpload(file)}>
                        <ReplayIcon fontSize="small" />
                      </RetryOverlay>
                    ) : (
                      <BlockedThumbOverlay
                        data-test-id={`upload-progress-blocked-${index}`}>
                        <BlockIcon fontSize="small" />
                      </BlockedThumbOverlay>
                    ))}
                </FileThumb>

                <FileInfo>
                  <FileName title={file.name}>{file.name}</FileName>
                  <FileSize>{formatSize(file.size)}</FileSize>

                  {(status === 'uploading' || status === 'idle') && (
                    <>
                      <ProgressBar
                        variant="determinate"
                        value={progress}
                        data-test-id={`upload-progress-bar-${index}`}
                      />
                      <ProgressLabel>
                        {progress}%{progress === 99 && <ProcessingDots />}
                      </ProgressLabel>
                      {progress === 99 && (
                        <ModerationLabel>
                          {tr('moderationInProgress')}
                        </ModerationLabel>
                      )}
                    </>
                  )}

                  {status === 'moderating' && (
                    <>
                      <ProgressBar
                        variant="indeterminate"
                        data-test-id={`upload-progress-bar-${index}`}
                      />
                      <ProgressLabel>{tr('checkingMedia')}</ProgressLabel>
                    </>
                  )}

                  {status === 'success' && uploadResult && (
                    <SuccessRow
                      data-test-id={`upload-progress-success-${index}`}>
                      <CheckCircleIcon />
                      <UuidLabel title={uploadResult.mediaId}>
                        {uploadResult.mediaId}
                      </UuidLabel>
                    </SuccessRow>
                  )}

                  {(status === 'error' || blocked) && (
                    <ErrorRow data-test-id={`upload-progress-error-${index}`}>
                      {blocked ? <BlockIcon /> : <ErrorOutlineIcon />}
                      <ErrorLabel title={translateErrorLabel(entry)}>
                        {translateErrorLabel(entry)}
                      </ErrorLabel>
                    </ErrorRow>
                  )}
                </FileInfo>

                <RemoveButton
                  aria-label={tr('removeFile', { name: file.name })}
                  data-test-id={`upload-progress-remove-${index}`}
                  onClick={() => handleRemove(file)}>
                  <DeleteIcon fontSize="small" />
                </RemoveButton>
              </FileRow>
            );
          })}
        </DialogContent>

        {allSettled && (
          <DialogFooter>
            <DoneButton
              disabled={!canFinish && !canRemoveBlocked}
              data-test-id="upload-progress-done"
              onClick={canRemoveBlocked ? handleRemoveBlocked : onClose}>
              {(canFinish && tr('done')) ||
                (canRemoveBlocked && tr('removeBlockedFiles')) ||
                tr('removeFailedFiles')}
            </DoneButton>
          </DialogFooter>
        )}
      </Dialog>

      <ImagePreviewDialog
        open={!!expandedSrc}
        onClose={() => setExpandedSrc(null)}
        aria-label={tr('imagePreview')}
        data-test-id="upload-progress-image-preview-dialog">
        {expandedSrc && (
          <ImagePreviewImg
            src={expandedSrc}
            alt={tr('imagePreview')}
            data-test-id="upload-progress-image-preview-img"
            onClick={() => setExpandedSrc(null)}
          />
        )}
      </ImagePreviewDialog>
    </>
  );
}
